import * as React from "react";
import {observer} from "mobx-react";
import styled from "styled-components";
import {withAppStores} from "../../../../../AppStores";
import type {TransportLayer} from "../../../../../data/TransportLayer";
import type {User} from "../../../../../data/models/User";
import type {AppState} from "../../../../../data/state/AppState";
import {XHRLoader} from "../../../../../utils/loader/XHRLoader";
import {FieldV5} from "../../../details/FieldV5";
import {ButtonV5} from "../../../button/ButtonV5";
import {DateFormatter} from "../../../../../utils/format/DateFormatter";
import {baseDistance, FLEXCENTER, FlexCenter, fontSize} from "../../../styles/styles";
import InfoIcon from "../../../icons/circle-information.svg?react";
import {ClickToEditInputV5} from "../../../input/clicktoedit/ClickToEditInputV5";
import {colorPalette} from "../../../styles/colorPalette";
import {FieldStyled} from "../../../details/Field.styled";
import {DetailsContentV5HeaderStyled} from "../../../details/DetailsContentV5HeaderStyled";
import {UserFormV5} from "./UserFormV5";

interface IUserDetailsContentProps {
	readonly users: User[];
	readonly appState?: AppState;
	readonly transport?: TransportLayer;
}

interface IUserDetailsContentStates {
	resendingInviteInProgress: boolean;
}

@withAppStores("appState", "transport")
@observer
export class UserDetailsContentV5 extends React.Component<IUserDetailsContentProps, IUserDetailsContentStates> {
	constructor(props: IUserDetailsContentProps) {
		super(props);
		this.state = {
			resendingInviteInProgress: false,
		};
	}

	private onResendInviteClick = async () => {
		this.setState({resendingInviteInProgress: true});

		const result = await this.props.transport.requestForOrganization({
			url: "users/resendinvite",
			method: XHRLoader.METHOD_POST,
			params: {
				userID: this.props.users[0].id,
			},
		});

		this.setState({resendingInviteInProgress: false});
	};

	public override render() {
		const {users, appState} = this.props;
		const user = users[0];

		return (
			<NodataUserDetailsContentStyled>
				{users.length === 0 && <NoDataStyled>Please select a User.</NoDataStyled>}
				{users.length === 1 && (
					<>
						<UserDetailsContentStyled>
							<DetailsContentV5HeaderStyled>
								<FlexCenter $gap="10px">
									<InfoIcon />
									<h2>Details</h2>
								</FlexCenter>
							</DetailsContentV5HeaderStyled>
							<div className="fieldSet">
								<FieldV5 label="Name">
									<ClickToEditInputV5
										value={user.fullName}
										disabled={true}
										className="disabledInputField"
									/>
								</FieldV5>

								<FieldV5 label="Email">
									<ClickToEditInputV5
										value={user.email}
										disabled={true}
										className="disabledInputField"
									/>
								</FieldV5>

								<UserDetailsContentButtonStyled>
									{appState.user?.isAdmin && user.status === "invited" && (
										<div className="addButtonContainer">
											<ButtonV5
												label={this.state.resendingInviteInProgress ? "Resending invite..." : "Resend Invite"}
												onClick={this.onResendInviteClick}
												className="addButton"
												disabled={this.state.resendingInviteInProgress}
											/>
										</div>
									)}
								</UserDetailsContentButtonStyled>

								<FieldV5 label="Status">
									<ClickToEditInputV5
										value={user.status}
										disabled={true}
										className="disabledInputField"
									/>
								</FieldV5>

								<FieldV5 label="Last Updated">
									<ClickToEditInputV5
										value={DateFormatter.format(user.lastModifiedAt)}
										disabled={true}
										className="disabledInputField"
									/>
								</FieldV5>
							</div>
						</UserDetailsContentStyled>
						<UserFormV5 users={users} />
					</>
				)}
			</NodataUserDetailsContentStyled>
		);
	}
}

export const NodataUserDetailsContentStyled = styled.div`
	.noData {
		color: ${colorPalette.gray.c500Primary};
		text-align: center;
		min-width: 410px;
		position: absolute;
		font-size: ${fontSize.xl};
		left: 50%;
		top: 50%;
		transform: translate(-50%, -60px);
	}
`;

const UserDetailsContentStyled = styled.div`
	${DetailsContentV5HeaderStyled} {
		padding-top: ${baseDistance.md};
		border-bottom: none;
		padding-bottom: 2px;
		margin-bottom: 10px;
	}

	padding-left: ${baseDistance.md};
	padding-right: ${baseDistance.md};

	${FieldStyled} {
		margin-bottom: ${baseDistance.sm};
	}
`;

const UserDetailsContentButtonStyled = styled.div`
	.addButtonContainer {
		width: 100%;
		display: flex;
		justify-content: end;
	}

	.addButton {
		right: 0;
		width: fit-content;
		background-color: ${colorPalette.white};
		color: ${colorPalette.primary.c500Primary};
		border: 1px solid ${colorPalette.primary.c500Primary};
		margin-bottom: 10px;
	}

	.addButton:hover {
		background-color: ${colorPalette.primary.c100};
	}
`;

export const NoDataStyled = styled.div`
	${FLEXCENTER};
	font-size: ${fontSize.xl};
	color: ${colorPalette.libraryColors.explosive};
	height: 80%;
	width: 100%;
	position: absolute;
`;
